import { useLocale } from "@/context/LocaleProvider";
import { usePersistedState, useProducts } from "@/hooks";
import useProductsColumns from "@/hooks/useProductsColumns";
import { DataGrid, DataGridClientPagination, GridToolbar } from "@/includes";
import { RootState } from "@/services/redux/store";
import { CircularProgress, useTheme } from "@mui/material";
import { GridColumnVisibilityModel } from "@mui/x-data-grid";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import productsColumns from "./columns";
import { MerchArrayProps } from "./types";

const MerchArray = ({
  selectedCategory,
  products,
  runningCollection,
}: MerchArrayProps) => {
  const theme = useTheme();
  const currency = useSelector((state: RootState) => state.catalog.currency);
  const { locale } = useLocale();
  const { t }: i18translateType = useTranslation();
  const siteId = useSelector((state: RootState) => state.site.siteId);
  const catalogColumns = useSelector(
    (state: RootState) => state.catalog.catalogColumns
  );
  const [columnVisibilityModel, setColumnVisibilityModel] = usePersistedState({
    key: `merchandising-${siteId}`,
    initialValue: {},
  });
  const { getCatalogColumns, getInitialColumns, getAllColumns } =
    useProductsColumns(locale, currency);
  const [datagridColumns, setDatagridColumns] = useState<Array<any>>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = usePersistedState({
    key: "draggableProductsListPageSize-2",
    initialValue: 100,
  });
  const columns = useMemo(() => {
    return productsColumns(theme, t, locale, currency, siteId);
  }, [theme]);
  const [productsToShowWithData, setProductsToShowWithData] = useState<
    Array<Dic<any>>
  >([]);
  useEffect(() => {
    const additionnalsColumns = getCatalogColumns(catalogColumns, t);
    setDatagridColumns([...columns, ...additionnalsColumns]);
  }, [columns]);
  const [initialData, setInitialData] = useState<Dic<any>>({});
  useEffect(() => {
    if (!isEmpty(datagridColumns)) {
      setInitialData(getInitialColumns(datagridColumns));
    }
  }, [datagridColumns]);
  useEffect(() => {
    if (isEmpty(columnVisibilityModel) && !isEmpty(initialData)) {
      setColumnVisibilityModel(initialData);
    } else if (!isEmpty(initialData)) {
      setColumnVisibilityModel({ ...initialData, ...columnVisibilityModel });
    }
  }, [initialData]);

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedCategory]);

  const productToShow = useMemo(() => {
    if (products)
      return products?.map((p: any, index: number) => ({
        ...p,
      }));
    return [];
  }, [products]);

  const { getProductsInfos, isGettingProductInfos } = useProducts();
  useEffect(() => {
    if (!isEmpty(productToShow)) {
      getProductsInfos(
        productToShow?.map((pdToShow: Dic<any>) => pdToShow.id)
      ).then((res: Dic<any>) => {
        setProductsToShowWithData(
          res.map((resProduct: Dic<any>, index: number) => {
            const associatedProduct =
              productToShow?.find(
                (pdToShow: Dic<any>) => pdToShow?.id === resProduct?.id
              ) ?? {};
            return {
              enrichedData: resProduct?.enrichedData,
              ...resProduct,
              isPinned: associatedProduct?.isPinned ?? false,
              index,
            };
          })
        );
      });
    }
  }, [productToShow]);

  const getTable = useMemo(() => {
    return (
      <DataGrid
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(model: GridColumnVisibilityModel) => {
          if (isEmpty(model)) {
            setColumnVisibilityModel(getAllColumns(columns));
          } else {
            setColumnVisibilityModel(model);
          }
        }}
        pageSize={pageSize}
        currentPage={currentPage}
        paginationType="client"
        columns={datagridColumns}
        rowCount={products?.length}
        rows={productsToShowWithData ?? []}
        loading={runningCollection || isGettingProductInfos}
        componentsProps={{
          pagination: {
            totalItems: products.length,
            pageSize,
            handleChangePageSize: setPageSize,
            currentPage,
            handleChangeCurrentPage: setCurrentPage,
          },
        }}
        components={{
          Pagination: DataGridClientPagination,
          Toolbar: GridToolbar,
        }}
      />
    );
  }, [
    productsToShowWithData,
    currentPage,
    pageSize,
    columnVisibilityModel,
    runningCollection,
    isGettingProductInfos,
  ]);
  return (
    <>{isEmpty(columnVisibilityModel) ? <CircularProgress /> : getTable}</>
  );
};

export default MerchArray;
